import { BookmarkAddIcon, PlusIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@components/SortableRow'
import ConfirmationModal, { DeletionTarget, Disclaimer } from '@components/ConfirmationModal'
import EmptyState, { EmptyStateButton } from '@components/EmptyState'
import { GetTravelSegmentResponse } from '@orbit/api-types'
import { Event } from '@types'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { DayHeader } from '../core/dayHeader'
import { dayTimeline } from '../core/timeline'
import EventDetailsModal from '../EventDetailsModal'
import ViewEventModal from '../ViewEventModal'
import {
	AddEventBar,
	BottomActions,
	BottomBar,
	DayActionButton,
	DayNote,
	DaySubtitle,
	DayTitle,
	EmptyDay,
	HeaderActions,
	PaneHeader,
	PaneRoot,
	TimelineList,
} from './DayPane.styles'
import { EditableEventRow, EventRow } from './EventRow'
import MoveEventModal, { DayOption } from './MoveEventModal'
import TravelRow from './TravelRow'
import { TestIds } from '@/testIds'

/** The selected day as the pane names it: which day, its header lines, and what its day info already holds. */
export interface SelectedDay {
	dayIndex: number
	header: DayHeader
	note: string | null
	hasName: boolean
}

export interface DayPaneProps {
	day: SelectedDay
	/** Every trip day, for the move dialog. */
	days: DayOption[]
	events: Event[]
	segments: GetTravelSegmentResponse[]
	/** Event ids the map can pin, so the card's pin glyph can grey out for the rest. */
	mappedEventIds: ReadonlySet<number>
	/** The event a map pin click asked to show; the pane scrolls to it and lights it up. `tick` changes per click, so the same pin can ask twice. */
	highlight: { eventId: number; tick: number } | null
	/** Absent in public mode: the pane is then read-only — no day info, add, reorder, move or delete, and events open read-only. */
	editing?: DayEditing
}

/** What the member mode can do to the selected day. */
export interface DayEditing {
	onEditDayInfo: () => void
	onAddEvent: () => void
	/** Absent when the trip has no ideas to import. */
	onImportIdea?: () => void
	onReorder: (eventId: number, eventIndex: number) => void
	onMoveToDay: (event: Event, dayIndex: number) => void
	/** Opens the edit-event flow for an event, from its details. */
	onEditEvent: (event: Event) => void
	onDeleteEvent: (event: Event) => void
}

/**
 * The selected day: its header and note, the rail of numbered events with the
 * travel gaps between them (drag to reorder within the day), and the bottom
 * Add bar — or, with nothing planned, the empty state in their place. Without
 * `editing` it is the same day, read-only.
 */
const DayPane: React.FC<DayPaneProps> = ({ day, days, events, segments, mappedEventIds, highlight, editing }) => {
	const { header, note, hasName } = day
	const [openEvent, setOpenEvent] = useState<Event | null>(null)
	const [movingEvent, setMovingEvent] = useState<Event | null>(null)
	const [deletingEvent, setDeletingEvent] = useState<Event | null>(null)
	const listRef = useRef<HTMLDivElement>(null)
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

	const rows = useMemo(() => dayTimeline(events, segments), [events, segments])

	// `events` is a dependency on purpose: a pin on another day selects that day
	// first, and its list arrives a render after the highlight does.
	useEffect(() => {
		if (!highlight) return
		const card = listRef.current?.querySelector<HTMLElement>(`[data-event-id="${highlight.eventId}"]`)
		card?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
	}, [highlight, events])

	const handleDragEnd = ({ active, over }: DragEndEvent) => {
		if (!over || active.id === over.id) return
		const to = events.findIndex(e => e.id === over.id)
		if (to !== -1) editing?.onReorder(Number(active.id), to)
	}

	// Modals live at the pane so a card can unmount (drag, refetch) without taking its dialog with it.
	const openedEvent = openEvent ? (events.find(e => e.id === openEvent.id) ?? openEvent) : null
	const modals = !editing ? (
		openedEvent && <ViewEventModal isOpen onClose={() => setOpenEvent(null)} event={openedEvent} />
	) : (
		<>
			{openedEvent && (
				<EventDetailsModal
					isOpen
					onClose={() => setOpenEvent(null)}
					onEdit={() => editing.onEditEvent(openedEvent)}
					onDelete={() => {
						setDeletingEvent(openEvent)
						setOpenEvent(null)
					}}
					event={openedEvent}
				/>
			)}
			{movingEvent && (
				<MoveEventModal
					isOpen
					eventName={movingEvent.name}
					currentDay={day.dayIndex}
					days={days}
					onClose={() => setMovingEvent(null)}
					onMove={dayIndex => {
						editing.onMoveToDay(movingEvent, dayIndex)
						setMovingEvent(null)
					}}
				/>
			)}
			<ConfirmationModal
				isOpen={deletingEvent !== null}
				onClose={() => setDeletingEvent(null)}
				onConfirm={() => {
					if (deletingEvent) editing.onDeleteEvent(deletingEvent)
					setDeletingEvent(null)
				}}
				title="Delete Event"
				message={
					<>
						Are you sure you want to delete '
						<DeletionTarget>{deletingEvent?.name}</DeletionTarget>'?
						<Disclaimer>This action cannot be undone</Disclaimer>
					</>
				}
				confirmText="Delete"
				cancelText="Cancel"
				variant="danger"
			/>
		</>
	)

	const paneHeader = (
		<PaneHeader>
			<div>
				<DayTitle>{header.title}</DayTitle>
				{header.subtitle && <DaySubtitle>{header.subtitle}</DaySubtitle>}
				{note && <DayNote>{note}</DayNote>}
			</div>
			{editing && (
				<HeaderActions>
					<DayActionButton type="button" onClick={editing.onEditDayInfo}>
						{hasName ? 'Edit day name' : 'Set day name'}
					</DayActionButton>
					<DayActionButton type="button" onClick={editing.onEditDayInfo}>
						{note ? 'Edit notes' : 'Add notes'}
					</DayActionButton>
				</HeaderActions>
			)}
		</PaneHeader>
	)

	if (events.length === 0) {
		return (
			<>
				<PaneRoot aria-label="Selected day">
					{paneHeader}
					<EmptyDay>
						{!editing ? (
							<EmptyState
								art="itinerary"
								variant="panel"
								title="Nothing planned for this day"
							/>
						) : (
							<EmptyState
								art="itinerary"
								variant="panel"
								title={`${header.place ?? header.title} is wide open`}
								description="Add an activity manually or pull one from your ideas"
							>
								{editing.onImportIdea && (
									<EmptyStateButton
										variant="secondary"
										onClick={editing.onImportIdea}
										data-testid={TestIds.itinerary.importIdeaButton}
									>
										<BookmarkAddIcon sx={{ fontSize: iconSizes.sm }} />
										Import idea
									</EmptyStateButton>
								)}
								<EmptyStateButton onClick={editing.onAddEvent}>
									<PlusIcon sx={{ fontSize: iconSizes.sm }} />
									Add event
								</EmptyStateButton>
							</EmptyState>
						)}
					</EmptyDay>
				</PaneRoot>
				{modals}
			</>
		)
	}

	const rail = rows.map((row, index) => {
		if (row.kind !== 'event') {
			return (
				<TravelRow key={`gap-${row.from.id}-${row.to.id}`} state={row.state} segment={row.segment} />
			)
		}
		const rowProps = {
			event: row.event,
			ordinal: events.indexOf(row.event) + 1,
			first: index === 0,
			last: index === rows.length - 1,
			mapped: mappedEventIds.has(row.event.id),
			highlighted: highlight?.eventId === row.event.id,
			onOpen: setOpenEvent,
		}
		return editing ? (
			<EditableEventRow
				key={row.event.id}
				{...rowProps}
				onMove={setMovingEvent}
				onDelete={setDeletingEvent}
			/>
		) : (
			<EventRow key={row.event.id} {...rowProps} />
		)
	})

	return (
		<>
			<PaneRoot aria-label="Selected day">
				{paneHeader}
				<TimelineList ref={listRef}>
					{!editing ? (
						rail
					) : (
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							modifiers={[restrictToVerticalAxis]}
							onDragEnd={handleDragEnd}
						>
							<SortableContext
								items={events.map(e => e.id)}
								strategy={verticalListSortingStrategy}
							>
								{rail}
							</SortableContext>
						</DndContext>
					)}
				</TimelineList>
			</PaneRoot>
			{editing && (
				<BottomBar>
					<BottomActions>
						{editing.onImportIdea && (
							<AddEventBar
								type="button"
								variant="secondary"
								onClick={editing.onImportIdea}
								data-testid={TestIds.itinerary.importIdeaButton}
							>
								<BookmarkAddIcon sx={{ fontSize: iconSizes.sm }} />
								Import Idea
							</AddEventBar>
						)}
						<AddEventBar type="button" onClick={editing.onAddEvent}>
							<PlusIcon sx={{ fontSize: iconSizes.sm }} />
							Add Event
						</AddEventBar>
					</BottomActions>
				</BottomBar>
			)}
			{modals}
		</>
	)
}

export default DayPane
